import type { ComponentManifest } from '@ayumia/contracts';
import { hasRunAction, type RunOutcome, type RunRequest, type RunSandbox } from '@ayumia/component-runtime';

/**
 * Bac à sable de `run` côté navigateur (ADR-083 §4) : un Web Worker dédié, créé à la première
 * exécution seulement — une vue sans `run` ne charge jamais l'interpréteur. L'interface n'est
 * jamais bloquée : le temps est borné dans l'interpréteur, et le fil est arrêté par l'hôte s'il ne
 * répond pas (RUN_TIMEOUT), puis recréé à l'exécution suivante.
 */

/** Marge accordée au chargement de l'interpréteur et aux échanges avec le fil. */
const HOST_GRACE_MS = 2_000;

type Pending = { resolve: (outcome: RunOutcome) => void; timer: ReturnType<typeof setTimeout> };

export class WorkerRunSandbox implements RunSandbox {
  private worker: Worker | null = null;
  private next = 0;
  private readonly pending = new Map<number, Pending>();

  constructor(private readonly spawn: () => Worker = () => new Worker(new URL('./run-worker.ts', import.meta.url), { type: 'module' })) {}

  execute(request: RunRequest): Promise<RunOutcome> {
    const worker = this.ensure();
    const id = (this.next += 1);
    return new Promise<RunOutcome>((resolve) => {
      const timer = setTimeout(() => {
        this.reset();
        resolve({ ok: false, error: { code: 'RUN_TIMEOUT', message: 'Le bac à sable ne répond pas : exécution arrêtée.' } });
      }, request.timeout_ms + HOST_GRACE_MS);
      this.pending.set(id, { resolve, timer });
      worker.postMessage({ id, request });
    });
  }

  private ensure(): Worker {
    if (this.worker !== null) return this.worker;
    const worker = this.spawn();
    worker.onmessage = (message: MessageEvent<{ id: number; outcome: RunOutcome }>) => {
      const entry = this.pending.get(message.data.id);
      if (entry === undefined) return;
      clearTimeout(entry.timer);
      this.pending.delete(message.data.id);
      entry.resolve(message.data.outcome);
    };
    worker.onerror = () => this.reset({ ok: false, error: { code: 'RUN_EXCEPTION', message: 'Le bac à sable s’est arrêté.' } });
    this.worker = worker;
    return worker;
  }

  /** Arrête le fil ; les exécutions en cours échouent. */
  private reset(outcome?: RunOutcome): void {
    this.worker?.terminate();
    this.worker = null;
    for (const [id, entry] of this.pending) {
      clearTimeout(entry.timer);
      this.pending.delete(id);
      entry.resolve(outcome ?? { ok: false, error: { code: 'RUN_TIMEOUT', message: 'Exécution arrêtée.' } });
    }
  }
}

let shared: WorkerRunSandbox | null = null;

/** Bac à sable partagé de l'application, créé au premier besoin. */
export function sharedRunSandbox(): RunSandbox {
  shared ??= new WorkerRunSandbox();
  return shared;
}

/** Vrai si le composant ou l'un des composants qu'il cite contient une action `run`. */
export function needsRunSandbox(manifest: ComponentManifest, resolve: (artifactId: string) => ComponentManifest | undefined): boolean {
  const seen = new Set<string>();
  const visit = (current: ComponentManifest): boolean => {
    if (hasRunAction(current)) return true;
    for (const dependency of current.dependencies ?? []) {
      if (seen.has(dependency.artifact_id)) continue;
      seen.add(dependency.artifact_id);
      const child = resolve(dependency.artifact_id);
      if (child !== undefined && visit(child)) return true;
    }
    return false;
  };
  return visit(manifest);
}
